import React from "react";
import { StyleSheet, ScrollView, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import useAuth from "@/hooks/useAuth";

export default function AccountStatusScreen() {
  const { colors } = useTheme();
  const { userStatus, signOut, user } = useAuth();

  const isPending = userStatus === "PENDING";
  const isInactive = userStatus === "INACTIVE";

  const getStatusConfig = () => {
    if (isPending) {
      return {
        icon: "hourglass-empty" as const,
        iconColor: colors.amber?.text || "#f59e0b",
        title: "Account Pending Approval",
        subtitle: "Your account is being reviewed by an administrator.",
        message:
          "Once your account is approved, you'll have full access to RockForce. This usually takes 1-2 business days.",
        cardBg: colors.amber?.background || "#fef3c7",
        cardBorder: colors.amber?.border || "#fcd34d",
      };
    }

    if (isInactive) {
      return {
        icon: "block" as const,
        iconColor: colors.ruby?.text || "#ef4444",
        title: "Account Inactive",
        subtitle: "Your account has been deactivated.",
        message:
          "Please contact your administrator if you believe this is an error or if you need to reactivate your account.",
        cardBg: colors.ruby?.background || "#fee2e2",
        cardBorder: colors.ruby?.border || "#fca5a5",
      };
    }

    // Default fallback
    return {
      icon: "info" as const,
      iconColor: colors.textSecondary,
      title: "Account Status",
      subtitle: "There's an issue with your account.",
      message: "Please contact support for assistance.",
      cardBg: colors.card,
      cardBorder: colors.border,
    };
  };

  const config = getStatusConfig();

  return (
    <ScrollView style={styles.container}>
      <View style={styles.content}>
        {/* Status Icon */}
        <View style={styles.iconContainer}>
          <View style={[styles.iconCircle, { backgroundColor: config.cardBg, borderColor: config.cardBorder }]}>
            <MaterialIcons name={config.icon} size={64} color={config.iconColor} />
          </View>
        </View>

        {/* Title and Subtitle */}
        <ThemedText style={[styles.title, { color: colors.text }]}>{config.title}</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>{config.subtitle}</ThemedText>

        {/* Info Card */}
        <View style={[styles.infoCard, { backgroundColor: config.cardBg, borderColor: config.cardBorder }]}>
          <MaterialIcons name="info-outline" size={24} color={config.iconColor} />
          <ThemedText style={[styles.infoText, { color: colors.text }]}>{config.message}</ThemedText>
        </View>

        {/* User Info Card */}
        <View style={[styles.userCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.userCardTitle, { color: colors.text }]}>Account Details</ThemedText>

          <View style={styles.userRow}>
            <MaterialIcons name="email" size={20} color={colors.textSecondary} />
            <ThemedText style={[styles.userRowText, { color: colors.text }]}>{user?.email || "—"}</ThemedText>
          </View>

          <View style={styles.userRow}>
            <MaterialIcons name="assignment-ind" size={20} color={colors.textSecondary} />
            <View style={[styles.statusBadge, { backgroundColor: config.cardBg, borderColor: config.cardBorder }]}>
              <ThemedText style={[styles.statusBadgeText, { color: config.iconColor }]}>
                {userStatus || "Unknown"}
              </ThemedText>
            </View>
          </View>
        </View>

        {/* Actions */}
        <View style={styles.actionsSection}>
          {isPending && (
            <View style={[styles.helpCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <MaterialIcons name="support-agent" size={24} color={colors.primary} />
              <View style={styles.helpContent}>
                <ThemedText style={[styles.helpTitle, { color: colors.text }]}>Need help?</ThemedText>
                <ThemedText style={[styles.helpText, { color: colors.textSecondary }]}>
                  Contact your administrator if your approval is taking longer than expected.
                </ThemedText>
              </View>
            </View>
          )}

          {isInactive && (
            <View style={[styles.helpCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <MaterialIcons name="contact-support" size={24} color={colors.primary} />
              <View style={styles.helpContent}>
                <ThemedText style={[styles.helpTitle, { color: colors.text }]}>Account Reactivation</ThemedText>
                <ThemedText style={[styles.helpText, { color: colors.textSecondary }]}>
                  If you need to reactivate your account, please reach out to your company administrator.
                </ThemedText>
              </View>
            </View>
          )}

          <Button
            text="Sign Out"
            onPress={signOut}
            icon="logout"
            iconSize={18}
            iconColor="white"
            style={[styles.signOutButton, { backgroundColor: colors.textSecondary }]}
            textStyle={{ color: "white", fontSize: 16 }}
          />
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
    alignItems: "center",
  },
  iconContainer: {
    marginTop: 40,
    marginBottom: 24,
  },
  iconCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 8,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: "center",
  },
  infoCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 24,
    width: "100%",
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  userCard: {
    width: "100%",
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 24,
    gap: 12,
  },
  userCardTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  userRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  userRowText: {
    fontSize: 14,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: "600",
  },
  actionsSection: {
    width: "100%",
    gap: 16,
  },
  helpCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  helpContent: {
    flex: 1,
    gap: 4,
  },
  helpTitle: {
    fontSize: 14,
    fontWeight: "600",
  },
  helpText: {
    fontSize: 13,
    lineHeight: 18,
  },
  signOutButton: {
    borderRadius: 8,
    minHeight: 48,
    marginTop: 8,
  },
});
